import { fetchDataWithPagination } from "@/lib/fetchDataWithPagination"
import { cache } from "react"

type DatasourceEntry = { name: string; value: string }

/**
 * Vaccine slug -> display name, read from the "vaccine-types" datasource.
 * The slugs are transliterated
 * (`baeltros`, `gula-febern`), so a label can only be recovered from the datasource
 */
export const getVaccineTypeNames = cache(async (): Promise<Record<string, string>> => {
  try {
    const entries = await fetchDataWithPagination<DatasourceEntry[]>({
      endpoint: "datasource_entries",
      customParams: { datasource: "vaccine-types", per_page: 50 }
    })

    return Object.fromEntries(
      entries.map(({ value, name }) => [String(value).trim(), String(name).trim()]).filter(([value]) => value)
    )
  } catch (error) {
    // A label falling back to the slug is worth far less than the page failing to render.
    console.error("Failed fetching vaccine type names:", error)
    return {}
  }
})

// QA/test markers. A value is treated as a test entry if it contains any marker as a token
// (bounded by start/end or "_"): covers qa_/_qa, test_/_test/_test_, and bare "qa"/"test".
// A real word that merely contains the letters (e.g. "attestation") is NOT excluded.
const TEST_MARKERS = ["qa", "test"]
const isTestValue = (value: string) => TEST_MARKERS.some(m => new RegExp(`(^|_)${m}(_|$)`, "i").test(value))

/**
 * Visit type short_name -> Swedish display name, read from the "clinic-visit-types" datasource.
 *
 * This doubles as an allowlist: a short_name absent from the result is never fetched or rendered on
 * a clinic page, so QA and test entries are stripped here rather than reaching a patient-facing page.
 *
 * Callers merge this over FALLBACK_VISIT_TYPE_NAMES, which is what keeps the core types resolving
 * when Storyblok is unreachable — an empty map here must degrade, never blank the section.
 */
export const getClinicVisitTypeNames = cache(async (): Promise<Record<string, string>> => {
  try {
    const entries = await fetchDataWithPagination<DatasourceEntry[]>({
      endpoint: "datasource_entries",
      customParams: { datasource: "clinic-visit-types", per_page: 50 }
    })

    const cleaned = entries
      .map(({ value, name }) => ({ value: String(value ?? "").trim(), name: String(name ?? "").trim() }))
      .filter(entry => entry.value && !isTestValue(entry.value))

    return Object.fromEntries(cleaned.map(({ value, name }) => [value, name]))
  } catch (error) {
    console.error("Failed fetching clinic visit type names:", error)
    return {}
  }
})
